import { DEFAULT_LOG_LEVEL } from './logger.config';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

const LEVELS: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 99,
};

const isLogLevel = (v: unknown): v is LogLevel =>
  typeof v === 'string' && v in LEVELS;

// Resolucion del nivel activo (precedencia, mayor a menor).
const resolveLevel = (): number => {
  // 1. Override en runtime por navegador, SIN redeploy.
  try {
    const o = localStorage.getItem('logLevel');
    if (isLogLevel(o)) return LEVELS[o];
  } catch {
    /* localStorage puede no estar disponible (SSR, modo privado, etc.) */
  }
  // 2. Variable de entorno del build (CRA).
  const env = process.env.REACT_APP_LOG_LEVEL;
  if (isLogLevel(env)) return LEVELS[env];
  // 3. Default versionado en el repo.
  if (isLogLevel(DEFAULT_LOG_LEVEL)) return LEVELS[DEFAULT_LOG_LEVEL];
  // 4. Fallback duro.
  return process.env.NODE_ENV === 'production' ? LEVELS.warn : LEVELS.debug;
};

const current = resolveLevel(); // se resuelve UNA vez al cargar el modulo

const noop = (): void => {};

// Gate: si el nivel activo permite, devuelve la fn bindeada; si no, un noop.
// Usamos .bind(console) (no un wrapper propio) para que DevTools siga mostrando
// el archivo/linea que LLAMO al logger, no logger.ts.
const at = (lvl: number, fn: (...args: any[]) => void): ((...args: any[]) => void) =>
  current <= lvl ? fn : noop;

export const logger = {
  log: at(LEVELS.debug, console.log.bind(console)),
  debug: at(LEVELS.debug, console.debug.bind(console)),
  info: at(LEVELS.info, console.info.bind(console)),
  warn: at(LEVELS.warn, console.warn.bind(console)),
  error: at(LEVELS.error, console.error.bind(console)),
};

// safeError: extrae solo campos seguros de un error (axios u otro), evitando
// filtrar Authorization, cookies o response.data al loguear.
export const safeError = (e: any) => {
  if (!e || typeof e !== 'object') return e;
  return {
    message: e.message,
    name: e.name,
    code: e.code,
    status: e.response?.status ?? e.status,
    method: e.config?.method,
    url: e.config?.url,
  };
};
